
import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTeacherAttendance } from '../../hooks/useTeacherAttendance';
import AttendanceCard from './AttendanceCard';
import AttendanceStats from './AttendanceStats';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const AttendanceList = () => {
  const { 
    selectedDate, 
    setSelectedDate, 
    getTeachersWithAttendance,
    isLoading
  } = useTeacherAttendance();
  
  const [activeTab, setActiveTab] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const teachersWithAttendance = getTeachersWithAttendance();
  
  const handlePreviousDay = () => {
    const prevDate = new Date(selectedDate);
    prevDate.setDate(prevDate.getDate() - 1);
    setSelectedDate(prevDate);
  };
  
  const handleNextDay = () => {
    const nextDate = new Date(selectedDate);
    nextDate.setDate(nextDate.getDate() + 1);
    setSelectedDate(nextDate);
  };
  
  const handleRefresh = () => {
    // Prevent multiple refreshes by checking if already refreshing
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    // Simulate refresh with a delay
    setTimeout(() => {
      setIsRefreshing(false);
    }, 800);
  };
  
  const filteredTeachers = teachersWithAttendance.filter(teacher => {
    if (activeTab === 'all') return true;
    return teacher.attendanceStatus === activeTab;
  });
  
  const isToday = selectedDate.toDateString() === new Date().toDateString();

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl flex items-center">
          <Calendar className="mr-2 h-5 w-5" />
          Teacher Attendance
        </CardTitle>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={handlePreviousDay}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className={`font-medium ${isToday ? 'text-primary' : ''}`}>
            {format(selectedDate, "EEEE, MMM d, yyyy")}
            {isToday && <span className="ml-2 text-sm text-primary">(Today)</span>}
          </span>
          <Button variant="outline" size="icon" onClick={handleNextDay}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            title="Refresh attendance data"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <AttendanceStats className="mb-6" />
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid grid-cols-4 w-full max-w-md mx-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="present" className="text-green-700">Present</TabsTrigger>
            <TabsTrigger value="late" className="text-yellow-700">Late</TabsTrigger>
            <TabsTrigger value="absent" className="text-red-700">Absent</TabsTrigger>
          </TabsList>
        </Tabs>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5].map((_, i) => (
              <div key={i} className="h-28 rounded-lg bg-muted animate-pulse"></div>
            ))}
          </div>
        ) : (
          <>
            {filteredTeachers.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <p>No teacher data available for this filter</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTeachers.map(teacher => (
                  <AttendanceCard 
                    key={teacher.id} 
                    teacher={teacher}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AttendanceList;
